/**
 * Pure TEA update for the outline chrome bridge (header "+" action, cancel
 * hook, trash refresh hook). Outline pages register callbacks on mount and
 * clear them on unmount.
 *
 * Shell: `useOutlineActionsStore.ts` (Pinia + useTea). No Cmds: the callbacks
 * are plain data that other shells invoke themselves.
 */
import { assertNever } from "../lib/assertNever";
import { nothing, type Maybe } from "../lib/maybe";
import type { UpdateFn } from "../lib/tea";

export type OutlineHeaderNewItem = {
  label: string;
  run: () => void;
  disabled: boolean;
};

export type OutlineHeaderNew = {
  label: string;
  run: () => void;
  /** Nothing runs the default action; Just opens the creation menu. */
  items: Maybe<OutlineHeaderNewItem[]>;
};

export type OutlineActionsModel = {
  /** "New chapter/note/entry", set by whichever of Tree/Notes/Database is
   * mounted; `nothing` on Storyboard/Trash. */
  readonly headerNew: Maybe<OutlineHeaderNew>;
  /** Cancels an in-progress create-flow on the active outline page. */
  readonly cancelCreating: Maybe<() => void>;
  /** Refreshes the Trash list after a restore; only Trash registers this. */
  readonly refreshTrash: Maybe<() => void>;
};

export const initialOutlineActionsModel: OutlineActionsModel = {
  headerNew: nothing,
  cancelCreating: nothing,
  refreshTrash: nothing,
};

export type OutlineActionsMsg =
  | {
      readonly type: "SetHeaderNew";
      readonly value: Maybe<OutlineHeaderNew>;
    }
  | {
      readonly type: "SetCancelCreating";
      readonly value: Maybe<() => void>;
    }
  | {
      readonly type: "SetRefreshTrash";
      readonly value: Maybe<() => void>;
    };

export function updateOutlineActions(
  model: OutlineActionsModel,
  msg: OutlineActionsMsg,
): readonly [OutlineActionsModel, readonly never[]] {
  switch (msg.type) {
    case "SetHeaderNew":
      return [{ ...model, headerNew: msg.value }, []];
    case "SetCancelCreating":
      return [{ ...model, cancelCreating: msg.value }, []];
    case "SetRefreshTrash":
      return [{ ...model, refreshTrash: msg.value }, []];
    default:
      return assertNever(msg);
  }
}

export const updateOutlineActionsFn: UpdateFn<
  OutlineActionsModel,
  OutlineActionsMsg,
  never
> = updateOutlineActions;
